import * as React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { CreateResponsiveStyle, DEVICE_SIZES, minSize } from 'rn-responsive-styles';

import { DefaultLayout } from '../DefaultLayout';
import { Button } from '../Button';
import { Text } from '../Text';
import { PletlyLogo } from '../../assets';
import { spacing, colors } from '../../theme';
import { Stepper } from '../Stepper';
import { OnboardingContainerProps } from './OnboardingContainerProps';

const IS_WELCOME_STEP_COUNTED = false;

export const OnboardingContainer: React.FC<OnboardingContainerProps> = ({
	heroImage,
	title,
	subtitle,
	children,
	ctaText,
	onNextClick,
	secondaryButtonText,
	onSecondaryClick,
	onSkipClick,
	isNextDisabled = false,
	hideLogo = false,
	itemsAlignment = 'left',
	actionsStyle,
	activeStep,
	steps,
}) => {
	const { t } = useTranslation();
	const styles = useStyles();

	return (
		<DefaultLayout style={styles.root}>
			<View style={[styles.card, styles[itemsAlignment]]}>
				{!hideLogo && (
					<PletlyLogo width={72} height={56} viewBox="0 0 158 123" style={styles.logoMargin} />
				)}
				{heroImage && <View style={styles.heroImage}>{heroImage}</View>}

				{activeStep !== undefined && (
					<Stepper
						steps={IS_WELCOME_STEP_COUNTED ? steps : steps - 1}
						activeStep={activeStep}
						showTextProgress
					/>
				)}

				<View style={styles.texts}>
					<Text category="h1" align={itemsAlignment}>
						{title}
					</Text>
					<Text category="s1" align={itemsAlignment}>
						{subtitle}
					</Text>
				</View>

				{children}

				<View style={[styles.actionsContainer, actionsStyle]}>
					<View style={[styles.actions, !onSkipClick && styles.full]}>
						{secondaryButtonText && (
							<Button
								color="primary"
								style={!onSkipClick && styles.full}
								onPress={onSecondaryClick}
							>
								{secondaryButtonText}
							</Button>
						)}
						{ctaText && (
							<Button
								style={!onSkipClick && styles.full}
								color="primary"
								variant="filled"
								onPress={onNextClick}
								disabled={isNextDisabled}
							>
								{ctaText}
							</Button>
						)}
					</View>

					{onSkipClick && (
						<TouchableOpacity style={styles.skipContainer} onPress={onSkipClick}>
							<Text category="p1">{t('common.skip')}</Text>
						</TouchableOpacity>
					)}
				</View>
			</View>
		</DefaultLayout>
	);
};

const useStyles = CreateResponsiveStyle(
	{
		root: {
			flexDirection: 'row',
			padding: spacing(2),
			justifyContent: 'center',
			width: '100%',
		},
		card: {
			flex: 1,
			backgroundColor: colors.white,
			borderRadius: 32,
			paddingVertical: spacing(4),
			paddingHorizontal: spacing(2),
		},
		center: {
			alignItems: 'center',
		},
		left: {
			alignItems: 'flex-start',
		},
		heroImage: {
			flexDirection: 'row',
			justifyContent: 'center',
			marginVertical: spacing(2),
		},
		logoMargin: {
			marginBottom: spacing(10),
		},
		texts: {
			marginVertical: spacing(4),
			gap: spacing(2),
		},
		subtitle: {
			textAlign: 'center',
			marginTop: spacing(1.5),
		},
		actionsContainer: {
			display: 'flex',
			flexDirection: 'column-reverse',
			justifyContent: 'space-between',
			width: '100%',
			marginTop: spacing(4),
			gap: spacing(2),
		},
		actions: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'flex-start',
			gap: spacing(2),
		},
		full: {
			flex: 1,
		},
		skipContainer: {
			flexDirection: 'row',
			justifyContent: 'center',
			alignItems: 'center',
		},
	},
	{
		[minSize(DEVICE_SIZES.MD)]: {
			root: {
				padding: spacing(4),
			},
			card: {
				paddingVertical: spacing(4),
				paddingHorizontal: spacing(4),
				maxWidth: 704,
			},
			actionsContainer: {
				flexDirection: 'row-reverse',
			},
			actions: {
				flexDirection: 'row',
				alignItems: 'center',
			},
		},
		[minSize(DEVICE_SIZES.LG)]: {
			root: {
				width: '100%',
			},
			card: {
				maxWidth: 840,
			},
		},
		[minSize(DEVICE_SIZES.XL)]: {
			card: {
				paddingVertical: spacing(10),
				paddingHorizontal: spacing(10),
			},
		},
	},
);
